
// import { createTheme } from '@mui/material/styles';
import { createTheme } from '@mui/material/styles';
import { adaptV4Theme } from '@mui/material/styles';
import { svSE } from '@mui/material/locale';

const hexToRgb = input => {
    input = input + "";
    input = input.replace("#", "");
    let hexRegex = /[0-9A-Fa-f]/g;
    if (!hexRegex.test(input) || (input.length !== 3 && input.length !== 6)) {
      throw new Error("input is not a valid hex color.");
    }
    if (input.length === 3) {
      let first = input[0];
      let second = input[1];
      let last = input[2];
      input = first + first + second + second + last + last;
    }
    input = input.toUpperCase(input);
    let first = input[0] + input[1];
    let second = input[2] + input[3];
    let last = input[4] + input[5];
    return (
      parseInt(first, 16) +
      ", " +
      parseInt(second, 16) +
      ", " +
      parseInt(last, 16)
    );
  };

const globalTheme = createTheme({
    palette: {
        primary: {
          light: '#0797ED',
          main: '#0797ED',
          dark: '#0797ED',
          contrastText: '#ffffff'
        },
        secondary: {
            light: 'rgb(73, 80, 87)',
            main: 'rgb(73, 80, 87)',
            dark: 'rgb(73, 80, 87)'
        },
        success: {
            light: '#6ec071',
            main: '#4caf50',
            dark: '#3d8b40',
            contrastText: '#ffffff'
        },
        error: {
            light: "#f77066",
            main: "#f44336",
            dark: "#ea1c0d",
            contrastText: '#ffffff'
        },
        warning: {
            light: "#ffad33",
            main: "#ff9800",
            dark: "#cc7a00",
            contrastText: '#ffffff'
        },
        common: {
            black: '#bbb',
            white: '#fff'
        },
        grey: {
            50: '#fafafa',
            100: '#f5f5f5',
            200: '#eeeeee',
            300: '#e0e0e0',
            400: '#bdbdbd',
            500: '#9e9e9e',
            600: '#757575',
            700: '#616161',
            800: '#424242',
            900: '#212121',
            A100: '#f5f5f5',
            A200: '#eeeeee',
            A400: '#bdbdbd',
            A700: '#616161',
        },
        text: {
            primary: 'rgb(73, 80, 87)',
            secondary: 'rgb(73, 80, 87)',
            disabled: 'rgb(73, 80, 87)',
            hint: 'rgb(73, 80, 87)'
        },
        typography: {
            fontSize: '0.875rem',
        },
        action: {
            active: 'rgb(73, 80, 87)',
            hover: 'rgba(0, 0, 0, 0.04)',
            hoverOpacity: '0.04',
            selected: 'rgba(0, 0, 0, 0.08)',
            selectedOpacity: '0.08',
            disabled: 'rgba(0, 0, 0, 0.26)',
            disabledBackground: 'rgba(0, 0, 0, 0.12)',
            disabledOpacity: '0.38',
            focus: 'rgba(0, 0, 0, 0.12)',
            focusOpacity: '0.12',
            activatedOpacity: '0.12'
        },
        tonalOffset: 1
    },
});

const muiTheme = createTheme(adaptV4Theme({
    ...globalTheme,
    props: {
        MuiInput: {
            disableUnderline: true
        },
        MuiInputLabel: {
            disableAnimation: true,
            shrink: false
        },
        MuiTextField: {
            variant: "standard"
        },
        MuiSelect: {
            variant: "standard"
        },
        MuiAutocomplete: {
            limitTags: 1
        },
        MuiChip: {
            color: 'primary'
        }
    },
    overrides:{
        MuiToolbar: {
            root: {
                paddingRight: "0px!important",
                paddingLeft: "0px!important",
                '& input': {
                    textAlign: "center!important"
                }
            },
            gutters: {
                paddingRight: "0px!important",
                paddingLeft: "0px!important"      
            }
        },
        MuiTablePagination: {
            styleOverrides: {
                '& .MuiTablePagination-selectLabel': {
                    margin: "0px!important"
                }
            }
        },
        MuiAutocomplete: {
            styleOverrides: {
                listbox: {
                    '& .MuiAutocomplete-option[aria-selected="true"]': {  // works
                    backgroundColor: '#bbb!important',
                    },
                }
            },
            root: {
                margin: '0 0 0 0',
                // position: 'relative',
                // paddingBottom: 'unset!important' // NEEDED?
                "& .MuiInput-root" : {
                    paddingBottom: "8px"
                }
            },
            // tag: {
            //     // backgroundColor: globalTheme.palette.primary.main
            // },
            endAdornment: {
                right: "12px!important"
            },
            popper: {
                margin: "10px 1px 10px 0px !important",
                "& > div": {
                  "&:before": {
                    borderBottomWidth: "1px !important",
                    borderBottomColor: globalTheme.palette.grey[200] + " !important"
                  },
                  "&:after": {
                    borderBottomColor: globalTheme.palette.primary.main + "!important"
                  }
                }
            },
            paper: {
                margin: "0px 20px 0px 20px",
                padding: "8px 4px 8px 4px!important",
                boxShadow: "0 2px 5px 0 rgba(0,0,0,0.26)!important",
            },
            option: {
                clear: 'both',
                margin: '4px 5px',
                display: 'block',
                padding: '10px 10px',
                fontSize: '0.8125rem',
                transition: 'all 150ms linear',
                fontWeight: '400',
                lineHeight: '2',
                whiteSpace: 'nowrap',
                borderRadius: '2px',
                paddingRight: '30px',
                color: globalTheme.palette.text.primary,
                // Hover
                "&.Mui-selected": {
                    color: globalTheme.palette.common.white,
                    "&.Mui-focusVisible": { color: globalTheme.palette.common.white }
                },
                "&.Mui-focused": {
                    backgroundColor: `${globalTheme.palette.primary.main}!important`,
                    borderColor: 'transparent',
                    color: `${globalTheme.palette.common.white}!important`,
                    boxShadow:
                    "0 12px 20px -10px rgba(" +
                    hexToRgb(globalTheme.palette.primary.main) +
                    ", 0.28), 0 4px 20px 0px rgba(" +
                    hexToRgb(globalTheme.palette.common.black) +
                    ", 0.12), 0 7px 8px -5px rgba(" +
                    hexToRgb(globalTheme.palette.primary.main) +
                    ", 0.2)"
                },
                // Selected
                '&[aria-selected="true"]': {
                    backgroundColor: `${globalTheme.palette.primary.main}!important`,
                    borderColor: 'transparent',
                    color: `${globalTheme.palette.common.white}!important`
                },
            },
            listbox: {
                padding: '0px 0px!important',
            }
        },
        MuiPaper: {
            root: {
                boxShadow: "0 2px 5px 0 rgba(0,0,0,0.26)!important",
                // marginTop: '15px!important'
                "&.MuiPaper-elevation0" : {
                    boxShadow: "unset!important"
                }
            }
        },
        MuiMenu: {
            paper: {
                marginTop: 15
            },
        },
        MuiMenuItem: {
            root: {
                borderRadius: 3,
                clear: 'both',
                margin: '4px 8px 4px 8px',
                // padding: '10px 10px',
                fontSize: '0.8125rem',
                transition: 'all 150ms linear',
                fontWeight: '400',
                lineHeight: '2',
                whiteSpace: 'nowrap',
                paddingRight: '30px',
                // backgroundColor: globalTheme.palette.common.white,
                "&.Mui-selected": {
                    color: globalTheme.palette.common.white,
                    "&.Mui-focusVisible": { color: globalTheme.palette.common.white }
                },
                '&:focus': {
                    backgroundColor: globalTheme.palette.primary.main,
                    borderRadius: 3,
                    '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
                        color: globalTheme.palette.common.white,
                    },
                    '& .MuiListItemIcon-root, & .MuiListItemText-secondary': {
                        color: globalTheme.palette.common.white,
                    },
                },
                '&:hover': {
                //   backgroundColor: `${globalTheme.palette.primary.main}!important`,
                  backgroundColor: `${globalTheme.palette.primary.main}!important`,
                  borderColor: 'transparent',
                  color: `${globalTheme.palette.common.white}!important`,
                  boxShadow:
                  "0 12px 20px -10px rgba(" +
                  hexToRgb(globalTheme.palette.primary.main) +
                  ", 0.28), 0 4px 20px 0px rgba(" +
                  hexToRgb(globalTheme.palette.common.black) +
                  ", 0.12), 0 7px 8px -5px rgba(" +
                  hexToRgb(globalTheme.palette.primary.main) +
                  ", 0.2)",
                  color: `${globalTheme.palette.common.white}!important`,
                  "& .MuiListItemIcon-root": {
                    color: "white"
                  }
                },
                '&[aria-selected="true"]': {
                    backgroundColor: `${globalTheme.palette.primary.main}!important`,
                    borderColor: 'transparent',
                    color: `${globalTheme.palette.common.white}!important`
                }
            },
        },
        // MuiChip: {
        //     root: {
        //         backgroundColor: globalTheme.palette.primary.main
        //     },
        //     label: {
        //         color: globalTheme.palette.common.white,
        //     },
        //     deleteIcon: {
        //         fill: globalTheme.palette.common.white,
        //     }
        // },
        MuiFormControl: {
            root: {
                margin: "20px 0 14px 0!important",
                paddingTop: "0px!important",
            }
        },
        MuiAppBar: {
            root: {
                marginTop: "0px!important"
            }
        },
        MuiFormLabel: {
            root: {
                color: '#AAA' ,
                "&.Mui-focused": {
                    "&$focused": {
                        "color": "#AAA"
                    }
                }
            }
        },
        MuiTextField: {
            root: {
                "& label" : {
                    top: "-42px !important",
                    left: "19px !important"
                }
            }
        },
        MuiInput: {
            root: {
                padding: "8px 20px 8px 20px",
                marginTop: "0px!important",
                border: "1px solid",
                borderColor: "#D2D2D2",
                borderRadius: 22,
                background: "#fff",
                '&:hover': {
                    boxShadow: "0 2px 8px 0px rgba(210, 210, 210.12), 0 1px 3px -3px rgba(210, 210, 210, 0.15)",
                    transition: "box-shadow 0.3s ease-in-out"
                },
                '&$focused': {
                    boxShadow: "0 4px 18px 0px rgba(210, 210, 210.12), 0 7px 10px -5px rgba(210, 210, 210, 0.15)"
                },
                "&.MuiInputBase-adornedStart" : {
                    paddingLeft: "45px"
                },
                "&.MuiInputBase-adornedEnd" : {
                    paddingRight: "45px"
                }
            },
            underline: {
                "&:after": { 
                    borderBottom: "unset!important"
                },
                "&:before": {
                    borderBottom: "unset!important"
                }
            },
            input: {
                fontSize: '0.875rem!important',
                padding: "0px!important",
                minHeight: "23px!important"
            }
        },
        MuiInputBase: {
            adornedStart: {
                paddingLeft: "45px!important"
            }
        },
        MuiSelect: {
            select: {
                "&:focus": {
                    background: "#fff"
                }
            },
            icon: {
                right: "10px!important"    
            }
        },
        MuiInputLabel: {
            root: {
                top: "-38px!important",
                left: "5px!important",
                color: '#AAA',
                fontSize: '0.875rem',
                "&.Mui-focused" : {
                    color: "#AAA"
                },
                "&$shrink": {
                    color: '#AAA!important' ,
                    "&$focused": {
                      color: '#AAA!important' ,
                    }
                },
                "&$erroredLabel": {
                    color: "orange!important"
                },  
                "&$focused" : {
                    color: '#AAA!important' 
                },
                "&.Mui-error": {
                    color: '#ea1c0d!important' 
                },
            }
        },
        MuiInputAdornment: {
            root: {
                right: "1px",
                position: "absolute",
                '& button': {
                    height: "35px!important",
                    width: "35px!important",
                    minWidth: "35px!important",
                    borderRadius: "50px"
                },
                '& button span': {
                    marginLeft: "2px!important"
                }
            },
            positionStart: {
                left: "13px !important",
                right: "unset!important",
                marginRight: "30px!important"
            },
            positionEnd: {
                right: "1px!important",
                bottom: "19.3px!important"
            }
        },
        MuiAccordion: {
            root: {
                boxShadow: 'unset!important'
            }
        },
        MuiFab: {
            root: {
                backgroundColor: globalTheme.palette.primary.main
            }
        },
        MuiTableCell: {
            alignCenter: {
                "& div" : {
                    justifyContent: 'center!important'
                }
            }
        }
    },
    svSE
}));

export { globalTheme, hexToRgb };
export default muiTheme
  